import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNetworkContext } from '@/contexts/NetworkContext';
import { FileType } from '@/types/data';
import { EmptyState } from '@/components/common/EmptyState';
import { useConnectionsData } from '@/hooks/useConnectionsData';
import { useHubsData } from '@/hooks/useHubsData';
import {
  ReactFlowProvider,
  Node,
  Edge,
  NodeChange,
  EdgeChange,
  applyNodeChanges,
  applyEdgeChanges,
  Connection as FlowConnection,
  addEdge,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import FlowWithControls from './FlowWithControls';
import { useLayoutedElements } from '@/hooks/useLayoutedElements';
import { useConnectionManagement } from '@/hooks/useConnectionManagement';
import { useNodeCreation } from '@/hooks/useNodeCreation';

const VisualiseTab: React.FC = () => {
  const { currentNetwork } = useNetworkContext();
  const { connections, handleRowsChange } = useConnectionsData(currentNetwork);
  const { hubs } = useHubsData(currentNetwork);
  const [isLoading, setIsLoading] = useState(true);

  // States for nodes and edges
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  
  // State for courier filter
  const [selectedCouriers, setSelectedCouriers] = useState<string[]>([]);

  // Custom hooks
  const { isLayouting, getLayoutedElements, applyLayout } = useLayoutedElements();
  const { createNodesFromLocations } = useNodeCreation();
  const { addConnection, removeConnection, createEdgesFromConnections } = useConnectionManagement(
    connections,
    handleRowsChange
  );

  // Extract unique couriers from connections
  const availableCouriers = useMemo(() => {
    const couriers = new Set<string>();
    connections.forEach((conn) => {
      if (conn.courier && conn.courier.trim()) {
        couriers.add(conn.courier.trim());
      }
    });
    return Array.from(couriers).sort();
  }, [connections]);

  // Set loading state when network changes
  useEffect(() => {
    // Set loading to true immediately
    setIsLoading(true);

    // Use requestAnimationFrame for a smoother transition
    // This will run after the browser has had a chance to render the loading state
    const frameId = window.requestAnimationFrame(() => {
      // Use another requestAnimationFrame to ensure we have a visible loading state
      // for at least one frame before setting loading to false
      window.requestAnimationFrame(() => {
        setIsLoading(false);
      });
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [currentNetwork]);

  // Generate nodes and edges from connections and standalone hubs/zones
  useEffect(() => {
    if (!hubs.length && (!connections || !connections.length)) return;

    // Create nodes from hubs and connections
    const newNodes = createNodesFromLocations(hubs, connections);

    // Create edges from connections
    const newEdges = createEdgesFromConnections();

    // Apply layout to the new nodes and edges
    applyLayout(newNodes, newEdges, setNodes, setEdges);
  }, [connections, hubs, createNodesFromLocations, createEdgesFromConnections, applyLayout]);

  // Filter edges based on selected couriers
  const filteredEdges = useMemo(() => {
    if (selectedCouriers.length === 0) return edges;
    
    return edges.filter((edge) => {
      const connection = edge.data?.connection as { courier?: string } | undefined;
      const edgeCourier = connection?.courier || edge.label;
      return edgeCourier && selectedCouriers.includes(edgeCourier as string);
    });
  }, [edges, selectedCouriers]);

  // Filter nodes based on filtered edges (only show nodes that are connected)
  const filteredNodes = useMemo(() => {
    if (selectedCouriers.length === 0) return nodes;
    
    // Create a set of node IDs that are part of filtered edges
    const connectedNodeIds = new Set<string>();
    filteredEdges.forEach((edge) => {
      connectedNodeIds.add(edge.source);
      connectedNodeIds.add(edge.target);
    });
    
    return nodes.filter((node) => connectedNodeIds.has(node.id));
  }, [nodes, filteredEdges, selectedCouriers]);

  // Handle node changes (e.g., position changes)
  const onNodesChange = useCallback((changes: NodeChange[]) => {
    // Update the nodes state based on the changes using applyNodeChanges utility
    setNodes((nds) => applyNodeChanges(changes, nds));
  }, []);

  // Handle edge changes
  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      // Update the edges state based on the changes using applyEdgeChanges utility
      const updatedEdges = applyEdgeChanges(changes, edges);
      setEdges(updatedEdges);

      // Process each change
      changes.forEach((change) => {
        if (change.type === 'remove') {
          // Find the edge that was removed
          const removedEdge = edges.find((edge) => edge.id === change.id);
          if (removedEdge && removedEdge.data?.connection) {
            // Remove the connection using the hook
            removeConnection(
              removedEdge.source,
              removedEdge.target,
              (removedEdge.data.connection as { courier: string }).courier
            );
          }
        }
        // Other change types are handled automatically or by other handlers
      });
    },
    [edges, removeConnection]
  );

  // Handle new connections
  const onConnect = useCallback(
    (params: FlowConnection) => {
      if (!params.source || !params.target) return;

      // Add the new edge to the edges array
      const newEdge = {
        ...params,
        id: `e${edges.length}`,
        label: 'default', // Default courier
        data: {
          connection: {
            courier: 'default',
            start: params.source,
            end: params.target,
            zone: '',
            priority: '1',
            book_on_scan: 'y',
            book_on_booking: 'n',
            book_on_tracking_from_previous_leg: 'n',
          },
        },
      };
      setEdges((eds) => addEdge(newEdge, eds as any));

      // Add the new connection to the connections array
      addConnection(params.source, params.target, 'default');
    },
    [edges, addConnection]
  );

  // Handle connection end
  const onConnectEnd = useCallback(() => {
    // This is called when the user releases the mouse after starting a connection
    // We don't need to do anything here as onConnect will be called if a valid connection is made
  }, []);

  // Handle opening fullscreen view
  const openFullscreen = useCallback(() => {
    // Store graph data in sessionStorage for the new window to access
    const graphData = {
      nodes: filteredNodes,
      edges: filteredEdges,
      selectedCouriers: selectedCouriers,
    };
    sessionStorage.setItem('fullscreenGraphData', JSON.stringify(graphData));

    // Open new window with fullscreen parameter
    const fullscreenUrl = `${window.location.origin}${window.location.pathname}?fullscreen=true`;
    window.open(fullscreenUrl, '_blank', 'toolbar=no,location=no,status=no,menubar=no,scrollbars=no,resizable=yes');
  }, [filteredNodes, filteredEdges, selectedCouriers]);

  if (!currentNetwork) {
    return (
      <EmptyState
        message="No network selected"
        subMessage="Please select a network from the left panel to visualize connections."
      />
    );
  }

  const connectionsFile = currentNetwork.files.find((file) => file.type === FileType.Connections);

  if (!connectionsFile) {
    return (
      <EmptyState
        message="No connections file found"
        subMessage="The selected network does not contain a connections file. Please select a different network or add a connections file to this network."
      />
    );
  }

  if (isLoading) {
    return (
      <div className="py-4 flex justify-center items-center h-64">
        <div className="animate-pulse text-indigo-600">Loading visualization data...</div>
      </div>
    );
  }

  // Handle courier filter toggle
  const toggleCourier = (courier: string) => {
    setSelectedCouriers((prev) =>
      prev.includes(courier) ? prev.filter((c) => c !== courier) : [...prev, courier]
    );
  };

  // Handle select all / clear all couriers
  const selectAllCouriers = () => {
    setSelectedCouriers(availableCouriers);
  };

  const clearAllCouriers = () => {
    setSelectedCouriers([]);
  };

  return (
    <div className="py-4">
      <div className="mb-4 flex justify-between items-start">
        <div>
          <h3 className="text-lg font-semibold text-gray-700">Network Visualization</h3>
          <p className="text-sm text-gray-500">
            Visualizing {filteredEdges.length} connections between {filteredNodes.length} locations
            {selectedCouriers.length > 0 && ` (filtered by ${selectedCouriers.length} courier${selectedCouriers.length > 1 ? 's' : ''})`}.
          </p>
        </div>
        <button
          onClick={openFullscreen}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded hover:bg-indigo-700 transition-colors shadow-sm"
          title="Open visualization in fullscreen mode in a new tab"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
            />
          </svg>
          Open Fullscreen
        </button>
      </div>

      {/* Courier Filter */}
      {availableCouriers.length > 0 && (
        <div className="mb-4 p-3 bg-indigo-50 rounded border border-indigo-200">
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-gray-700">Filter by Courier:</label>
            <div className="flex gap-2">
              <button
                onClick={selectAllCouriers}
                className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
              >
                Select All
              </button>
              <span className="text-gray-300">|</span>
              <button
                onClick={clearAllCouriers}
                className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
              >
                Clear All
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {availableCouriers.map((courier) => (
              <button
                key={courier}
                onClick={() => toggleCourier(courier)}
                className={`px-3 py-1 text-sm rounded-full transition-colors ${
                  selectedCouriers.includes(courier)
                    ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                    : 'bg-white text-gray-700 border border-gray-300 hover:border-indigo-400'
                }`}
              >
                {courier}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="border rounded overflow-hidden shadow-sm mb-4" style={{ height: '500px' }}>
        <ReactFlowProvider>
          <FlowWithControls
            nodes={filteredNodes}
            edges={filteredEdges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onConnectEnd={onConnectEnd}
            getLayoutedElements={getLayoutedElements}
            setNodes={setNodes}
            setEdges={setEdges}
            isLayouting={isLayouting}
          />
        </ReactFlowProvider>
      </div>

      <div className="text-sm p-2 rounded bg-gray-50 text-gray-500">
        <p>
          <span className="font-medium">Note:</span> Drag nodes to reposition them or use the
          &#34;Apply Auto Layout&#34; button to automatically arrange the network. Changes to
          connections will be reflected in the Connections tab.
        </p>
      </div>
    </div>
  );
};

export default VisualiseTab;
